import React, { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/Tables/DataTable';
import InvoiceForm from '../components/Forms/InvoiceForm';
import { Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const Invoices = () => {
    const { currency } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [invoiceProfile, setInvoiceProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [invoicesRes, customersRes, profilesRes] = await Promise.all([
                api.get('/invoices'),
                api.get('/customers?limit=100'),
                api.get('/invoice-profiles')
            ]);
            setInvoices(invoicesRes.data);
            setCustomers(customersRes.data.customers);
            if (profilesRes.data && profilesRes.data.length > 0) {
                setInvoiceProfile(profilesRes.data[0]);
            }
        } catch (err) {
            console.error('Error fetching data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreate = () => {
        setEditingInvoice(null);
        setShowForm(true);
    };

    const handleEdit = (invoice: any) => {
        setEditingInvoice(invoice);
        setShowForm(true);
    };

    const handleDelete = async (invoice: any) => {
        if (window.confirm('Are you sure you want to delete this invoice?')) {
            try {
                await api.delete(`/invoices/${invoice.id}`);
                fetchData();
            } catch (err) {
                console.error('Error deleting invoice', err);
            }
        }
    };

    const columns = [
        { header: 'Invoice Number', accessor: 'invoiceNumber' },
        {
            header: 'Customer',
            accessor: (item: any) => item.customer?.displayName || 'Unknown'
        },
        {
            header: 'Date',
            accessor: (item: any) => new Date(item.date).toLocaleDateString()
        },
        { header: 'Status', accessor: 'status' },
        {
            header: 'Total Amount',
            accessor: (item: any) => formatCurrency(item.totalAmount, currency)
        }
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Invoices</h1>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                >
                    <Plus size={18} />
                    New Invoice
                </button>
            </div>

            <DataTable
                data={invoices}
                columns={columns}
                total={invoices.length}
                page={1}
                limit={50}
                onPageChange={() => { }}
                onEdit={handleEdit}
                onDelete={handleDelete}
                loading={loading}
            />

            {showForm && (
                <InvoiceForm
                    onClose={() => setShowForm(false)}
                    onSuccess={() => {
                        setShowForm(false);
                        fetchData();
                    }}
                    invoice={editingInvoice}
                    customers={customers}
                    invoiceProfile={invoiceProfile}
                    invoices={invoices}
                />
            )}
        </div>
    );
};

export default Invoices;
